import type { ChartGranularity } from '@/types';
import { PageType } from '@/types';
import { LevelOfDetail } from '@/types';

export enum RevenueDashboardWidget {
  NetworkSelect = 'networkSelect',
  ShowSelect = 'showSelect',
  EpisodeSelect = 'episodeSelect',
  PlatformSelect = 'platformSelect',
  DateSelect = 'dateSelect',
  CampaignType = 'campaignType',
  CampaignTable = 'campaignTable',
  DownloadDrawer = 'downloadDrawer',
  TotalEstimatedImpressions = 'totalEstimatedImpressions',
  ShowPerformance = 'showPerformance',
  CampaignTypes = 'campaignTypes',
  TopAdvertisers = 'topAdvertisers',
  Campaigns = 'campaigns',
  YearsTabs = 'yearsTabs',
  PayoutTable = 'payoutTable',
  PayoutSinglePageHeading = 'payoutSinglePageHeading',
  PayoutMonthHighlights = 'payoutMonthHighlights',
  PayoutsOverTime = 'payoutsOverTime',
  GenderDemographics = 'genderDemographics',
  AgeDemographics = 'ageDemographics',
  LocationDemographics = 'locationDemographics',
  TopPlayers = 'topPlayers',
  TopDevices = 'topDevices',
  SocialProfiles = 'socialProfiles',
  ContentPerformanceChart = 'contentPerformanceChart',
  ContentPerformanceEpisodesTable = 'contentPerformanceEpisodesTable',
  ContentPerformanceDownloadDrawer = 'contentPerformanceDownloadDrawer',
  AudienceDownloadDrawer = 'audienceDownloadDrawer',
  ContentPerformanceDataLevelTabs = 'contentPerformanceDataLevelTabs',
}

interface RevenueDashboardWebVitalsEventsProps {
  metricName: 'CLS' | 'INP' | 'LCP';
  value: number;
  delta: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  navigationType: string;
  page: string;
  networkId: string;
}

interface RevenueDashboardNetworkSelectEventsProps extends RevenueDashboardWidgetEventsProps {
  networkName: string;
  /** Selected network ids — more reliable than names for debugging multi-network selections. */
  networkIds?: string[];
}

interface RevenueDashboardEpisodesFilterSelectedEventsProps extends RevenueDashboardWidgetEventsProps {
  episodeId: string | undefined;
}

interface RevenueDashboardWidgetGlobalEventsProps {
  page: PageType;
}

interface RevenueDashboardWidgetEventsProps {
  widget: RevenueDashboardWidget;
  page: PageType;
}

interface RevenueDashboardShowsFilterSelectedEventsProps extends RevenueDashboardWidgetEventsProps {
  showId: string;
  showName: string;
}

interface RevenueDashboardPlatformFilterSelectedEventsProps extends RevenueDashboardWidgetEventsProps {
  platforms: string;
}

interface RevenueDashboardDateRangeEventsProps extends RevenueDashboardWidgetEventsProps {
  start: string;
  end: string;
}

interface RevenueDashboardDateRangePresetEventsProps extends RevenueDashboardWidgetEventsProps {
  preset: string;
}

interface RevenueDashboardDemographicsRadioSelectedEventsProps extends RevenueDashboardWidgetEventsProps {
  platform: string;
  demographics: 'gender' | 'age' | 'location';
}

interface RevenueDashboardDemographicsChartTooltipHoverEventsProps extends RevenueDashboardWidgetEventsProps {
  platform: string;
  demographics: 'gender' | 'age' | 'location';
  segment: string;
  percent?: string;
  plays?: string;
}

interface RevenueDashboardAudienceModalToggleEventsProps extends RevenueDashboardWidgetEventsProps {
  enabled: boolean;
}

interface RevenueDashboardAudienceModalGranularityEventsProps
  extends RevenueDashboardWidgetEventsProps {
  granularity: ChartGranularity;
}

interface RevenueDashboardAudienceModalRowSelectionEventsProps
  extends RevenueDashboardWidgetEventsProps {
  rowId: string;
  selected: boolean;
}

interface RevenueDashboardAudienceModalSelectAllEventsProps
  extends RevenueDashboardWidgetEventsProps {
  selected: boolean;
}

interface RevenueDashboardAudienceModalDeviceSelectionEventsProps
  extends RevenueDashboardWidgetEventsProps {
  device: string;
  selected: boolean;
}

interface RevenueDashboardDownloadDrawerDownloadEventsProps
  extends RevenueDashboardWidgetEventsProps {
  breakdown: LevelOfDetail;
  networkName: string;
  shows: string[] | string;
  start: string;
  end: string;
  includesShows: boolean;
  includesCampaignTypes: boolean;
  includesAdvertisers: boolean;
  includesCampaigns: boolean;
}

interface RevenueDashboardDownloadDrawerDownloadFailedEventsProps
  extends RevenueDashboardDownloadDrawerDownloadEventsProps {
  errorCode: string;
}

interface RevenueDashboardTableSortColumnClickedEventsProps
  extends RevenueDashboardWidgetEventsProps {
  column: string;
  order: 'asc' | 'desc';
}

interface RevenueDashboardTablePaginateEventsProps extends RevenueDashboardWidgetEventsProps {
  pageNumber: number;
}

interface RevenueDashboardFilteredDownloadClickedEventsProps
  extends RevenueDashboardWidgetEventsProps {
  networkName: string;
  start: string;
  end: string;
}

interface RevenueDashboardPayoutsFilteredDownloadClickedEventsProps
  extends RevenueDashboardWidgetEventsProps {
  networkName: string;
  year: string;
  month: string;
}

interface RevenueDashboardCampaignsTableFilterClickedEventsProps
  extends RevenueDashboardWidgetEventsProps {
  type: 'show' | 'advertiser' | 'campaign_type';
}
interface RevenueDashboardCampaignsTableFilterOptionClickedEventsProps
  extends RevenueDashboardCampaignsTableFilterClickedEventsProps {
  ids: string[];
}

interface RevenueDashboardCampaignTypesBarChartHoverEventsProps
  extends RevenueDashboardWidgetEventsProps {
  campaignType: string;
}

interface RevenueDashboardPayoutsOverTimeChartTooltipOpenedEventsProps
  extends RevenueDashboardWidgetEventsProps {
  month: string;
}

interface RevenueDashboardPayoutsYearSelectedEventsProps extends RevenueDashboardWidgetEventsProps {
  year: string;
}

interface RevenueDashboardPayoutsMonthSelectedEventsProps
  extends RevenueDashboardWidgetEventsProps {
  month: string;
  year: string;
  isLastMonthsPayout: boolean;
}

interface RevenueDashboardContentPerformanceChartGranularityEventsProps
  extends RevenueDashboardWidgetEventsProps {
  granularity: ChartGranularity;
}

interface RevenueDashboardContentPerformanceChartToggleEventsProps
  extends RevenueDashboardWidgetEventsProps {
  toggle: 'publishDates' | 'displayTotal';
  enabled: boolean;
}

interface RevenueDashboardContentPerformanceChartTabEventsProps
  extends RevenueDashboardWidgetEventsProps {
  tab: 'totals' | 'post-launch';
}

interface RevenueDashboardContentPerformanceLevelTabEventsProps
  extends RevenueDashboardWidgetEventsProps {
  tab: 'networks' | 'shows' | 'episodes';
  /** What caused the level-tab change — a filter multi-select or an explicit tab click. */
  trigger: 'filter' | 'tabClick';
}

interface RevenueDashboardContentPerformanceChartLaunchWindowEventsProps
  extends RevenueDashboardWidgetEventsProps {
  windowDays: 7 | 30;
}

interface RevenueDashboardContentPerformanceChartPostLaunchTooltipHoverEventsProps
  extends RevenueDashboardWidgetEventsProps {
  windowDays: 7 | 30;
}

interface RevenueDashboardContentPerformanceTablePageSizeEventsProps
  extends RevenueDashboardWidgetEventsProps {
  pageSize: number;
}

interface RevenueDashboardContentPerformanceEpisodeRowEventsProps
  extends RevenueDashboardWidgetEventsProps {
  episodeId: string;
  expanded: boolean;
}

interface RevenueDashboardContentPerformanceDownloadDrawerDownloadEventsProps
  extends RevenueDashboardWidgetEventsProps {
  /** Every network the export covers, in selection order. */
  networkIds: string[];
  /** Human-readable labels parallel to `networkIds` (same length/order). */
  networkNames?: string[];
  shows: string[] | string;
  start: string;
  end: string;
  includeEpisodeTotals?: boolean;
  includeEpisodePlatforms?: boolean;
  includeLaunchFirst7?: boolean;
  includeLaunchFirst30?: boolean;
}

interface RevenueDashboardContentPerformanceDownloadDrawerDownloadFailedEventsProps
  extends RevenueDashboardContentPerformanceDownloadDrawerDownloadEventsProps {
  errorCode: string;
}

interface RevenueDashboardAudienceDownloadDrawerDownloadEventsProps
  extends RevenueDashboardWidgetEventsProps {
  networkId: string;
  networkName?: string;
  shows: string[] | string;
  start: string;
  end: string;
  reports: string[];
}

interface RevenueDashboardAudienceDownloadDrawerDownloadFailedEventsProps
  extends RevenueDashboardAudienceDownloadDrawerDownloadEventsProps {
  errorCode: string;
}

const SHARED_PAGE_LABEL = {
  earnings: 'Earnings',
  audience: 'Audience',
  contentPerformance: 'Content performance',
} as const;

const SHARED_EVENT_SUFFIX = {
  showsFilter: {
    opened: 'shows filter opened',
    selected: 'shows filter selected',
  },
  date: {
    opened: 'date opened',
    applied: 'date applied',
    presetSelected: 'date preset selected',
  },
} as const;

type SharedPageKey = keyof typeof SHARED_PAGE_LABEL;
type SharedEventGroupKey = keyof typeof SHARED_EVENT_SUFFIX;
type SharedEventKey<G extends SharedEventGroupKey> = Extract<
  keyof (typeof SHARED_EVENT_SUFFIX)[G],
  string
>;
type SharedPageEvents<P extends SharedPageKey, G extends SharedEventGroupKey> = {
  [E in SharedEventKey<G>]: `${(typeof SHARED_PAGE_LABEL)[P]} ${((typeof SHARED_EVENT_SUFFIX)[G][E] & string)}`;
};

const buildSharedPageEvents = <P extends SharedPageKey, G extends SharedEventGroupKey>(
  pageKey: P,
  group: G
): SharedPageEvents<P, G> => {
  const pageLabel = SHARED_PAGE_LABEL[pageKey];
  const suffixes = SHARED_EVENT_SUFFIX[group];
  const eventKeys = Object.keys(suffixes) as SharedEventKey<G>[];
  const groupEvents = {} as SharedPageEvents<P, G>;

  for (const eventKey of eventKeys) {
    groupEvents[eventKey] = `${pageLabel} ${suffixes[eventKey]}` as SharedPageEvents<P, G>[typeof eventKey];
  }

  return groupEvents;
};

const SHARED_SHOWS_FILTER_EVENT = {
  earnings: buildSharedPageEvents('earnings', 'showsFilter'),
  audience: buildSharedPageEvents('audience', 'showsFilter'),
  contentPerformance: buildSharedPageEvents('contentPerformance', 'showsFilter'),
} as const;

const SHARED_DATE_EVENT = {
  earnings: buildSharedPageEvents('earnings', 'date'),
  audience: buildSharedPageEvents('audience', 'date'),
  contentPerformance: buildSharedPageEvents('contentPerformance', 'date'),
} as const;

export const MIXPANEL_EVENT = {
  rageClick: {
    detected: 'Rage click detected',
  },
  networkSelect: {
    opened: 'Network select opened',
    selected: 'Network select selected',
  },
  earnings: {
    showsFilter: SHARED_SHOWS_FILTER_EVENT.earnings,
    date: SHARED_DATE_EVENT.earnings,
    platformFilter: {
      opened: 'Platform filter opened',
      selected: 'Platform filter selected',
    },
    episodesFilter: {
      opened: 'Episodes filter opened',
      selected: 'Episodes filter selected',
    },
    downloadDrawer: {
      opened: 'Earnings download drawer opened',
      downloadSuccess: 'Earnings download drawer download success',
      downloadFailed: 'Earnings download drawer download failed',
      dateOpened: 'Earnings download drawer date select opened',
      dateApplied: 'Earnings download drawer date applied',
      datePresetSelected: 'Earnings download drawer date preset selected',
    },
    totalEstimatedImpressions: {
      tooltipHover: 'Earnings total estimated impressions tooltip hover',
    },
    showPerformance: {
      moreDetailsClicked: 'Earnings show performance more details clicked',
      closed: 'Earnings show performance closed',
      downloadClicked: 'Earnings show performance download clicked',
      dateOpened: 'Earnings show performance date select opened',
      dateApplied: 'Earnings show performance date applied',
      datePresetSelected: 'Earnings show performance date preset selected',
      tableSortColumnClicked: 'Earnings show performance table sort column clicked',
      tablePaginateClicked: 'Earnings show performance table select paginate clicked',
    },
    campaignTypes: {
      barChartHover: 'Earnings campaign types bar chart hover',
      moreDetailsClicked: 'Earnings campaign types more details clicked',
      modalClosed: 'Earnings campaign types closed',
      downloadClicked: 'Earnings campaign types download clicked',
      showFilterOpened: 'Earnings campaign types show filter opened',
      showFilterSelected: 'Earnings campaign types show filter selected',
      dateOpened: 'Earnings campaign types date select opened',
      dateApplied: 'Earnings campaign types date applied',
      datePresetSelected: 'Earnings campaign types date preset selected',
      tableSortColumnClicked: 'Earnings campaign types table sort column clicked',
    },
    topAdvertisers: {
      moreAdvertisersClicked: 'Earnings top advertisers more advertisers clicked',
    },
    campaignsTable: {
      filterClicked: 'Earnings campaigns table filter clicked',
      filterOptionClicked: 'Earnings campaigns table filter option clicked',
      sortColumnClicked: 'Earnings campaigns table sort column clicked',
      paginateClicked: 'Earnings campaigns table paginate clicked',
    },
  },
  payouts: {
    overTimeChart: {
      tooltipOpened: 'Payouts over time chart tooltip opened',
    },
    yearTab: {
      clicked: 'Payouts year tab clicked',
    },
    tableMonthRow: {
      clicked: 'Payouts table month row clicked',
    },
    highlights: {
      includeEarningsTooltipHover: 'Payouts include earnings tooltip hover',
      vendorStatementsDownloadClicked: 'Payouts vendor statements download clicked',
      vendorStatementsDownloadSuccess: 'Payouts vendor statements download success',
    },
    campaignTypes: {
      barChartHover: 'Payouts campaign types bar chart hover',
      moreDetailsClicked: 'Payouts campaign types more details clicked',
      downloadClicked: 'Payouts campaign types download clicked',
      modalClosed: 'Payouts campaign types modal closed',
      tableSortColumnClicked: 'Payouts campaign types table sort column clicked',
      payoutDropdownClicked: 'Payouts payout dropdown clicked',
    },
    campaignsTable: {
      filterClicked: 'Payouts campaign table filter clicked',
      filterOptionClicked: 'Payouts campaign table filter option clicked',
      sortColumnClicked: 'Payouts campaign table sort column clicked',
      paginateClicked: 'Payouts campaign table paginate clicked',
    },
  },
  audience: {
    showsFilter: SHARED_SHOWS_FILTER_EVENT.audience,
    date: SHARED_DATE_EVENT.audience,
    demographics: {
      platformRadioSelected: 'Audience demographics platform radio selected',
      chartTooltipHover: 'Audience demographics chart tooltip hover',
    },
    socialProfiles: {
      connectMarketplaceProfilesClicked: 'Connect marketplace profiles clicked',
    },
    ageModal: {
      opened: 'Audience age modal opened',
      downloadClicked: 'Audience age modal download clicked',
    },
    genderModal: {
      opened: 'Audience gender modal opened',
      downloadClicked: 'Audience gender modal download clicked',
    },
    locationModal: {
      opened: 'Audience location modal opened',
      downloadClicked: 'Audience location modal download clicked',
      hideSocialsToggled: 'Audience location modal hide socials toggled',
    },
    topPlayersModal: {
      opened: 'Audience top players modal opened',
      downloadClicked: 'Audience top players modal download clicked',
      granularityChanged: 'Audience top players modal granularity changed',
      hideSocialsToggled: 'Audience top players modal hide socials toggled',
      rowSelectionToggled: 'Audience top players modal row selection toggled',
      selectAllToggled: 'Audience top players modal select all toggled',
    },
    topDevicesModal: {
      opened: 'Audience top devices modal opened',
      downloadClicked: 'Audience top devices modal download clicked',
      deviceSelectionToggled: 'Audience top devices modal device selection toggled',
    },
    downloadDrawer: {
      opened: 'Audience download drawer opened',
      downloadSuccess: 'Audience download drawer download success',
      downloadFailed: 'Audience download drawer download failed',
    },
  },
  contentPerformance: {
    showsFilter: SHARED_SHOWS_FILTER_EVENT.contentPerformance,
    date: SHARED_DATE_EVENT.contentPerformance,
    chart: {
      granularityChanged: 'Content performance chart granularity changed',
      publishDatesToggled: 'Content performance chart publish dates toggled',
      displayTotalToggled: 'Content performance chart display total toggled',
      tabChanged: 'Content performance chart tab changed',
      launchWindowChanged: 'Content performance chart launch window changed',
      postLaunchTooltipHover: 'Content performance chart post launch tooltip hover',
    },
    episodesTable: {
      sortColumnClicked: 'Content performance episodes table sort column clicked',
      paginateClicked: 'Content performance episodes table paginate clicked',
      pageSizeChanged: 'Content performance episodes table page size changed',
    },
    levelTabs: {
      tabChanged: 'Content performance level tab changed',
    },
    episodeRow: {
      expandToggled: 'Content performance episode row expand toggled',
    },
    downloadDrawer: {
      opened: 'Content performance download drawer opened',
      downloadSuccess: 'Content performance download drawer download success',
      downloadFailed: 'Content performance download drawer download failed',
      /**
       * @deprecated Legacy `files=` drawer only.
       * TODO: remove once `newDownloadDrawers` is enabled for everyone.
       */
      dateOpened: 'Content performance download drawer date select opened',
      /** @deprecated Legacy `files=` drawer only. TODO: remove with `dateOpened`. */
      dateApplied: 'Content performance download drawer date applied',
      /** @deprecated Legacy `files=` drawer only. TODO: remove with `dateOpened`. */
      datePresetSelected: 'Content performance download drawer date preset selected',
    },
  },
  webVitals: {
    reported: 'Web vitals reported',
  },
} as const;

type DeepValues<T> = T extends string ? T : { [K in keyof T]: DeepValues<T[K]> }[keyof T];

export type MixpanelEvent = DeepValues<typeof MIXPANEL_EVENT>;

type RageClickEventProps = {
  [MIXPANEL_EVENT.rageClick.detected]: RevenueDashboardWidgetGlobalEventsProps;
};

type NetworkSelectEventProps = {
  [MIXPANEL_EVENT.networkSelect.opened]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.networkSelect.selected]: RevenueDashboardNetworkSelectEventsProps;
};

type EarningsPageEventProps = {
  [MIXPANEL_EVENT.earnings.showsFilter.opened]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.earnings.showsFilter.selected]: RevenueDashboardShowsFilterSelectedEventsProps;
  [MIXPANEL_EVENT.earnings.date.opened]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.earnings.date.applied]: RevenueDashboardDateRangeEventsProps;
  [MIXPANEL_EVENT.earnings.date.presetSelected]: RevenueDashboardDateRangePresetEventsProps;
  [MIXPANEL_EVENT.earnings.platformFilter.opened]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.earnings.platformFilter.selected]: RevenueDashboardPlatformFilterSelectedEventsProps;
  [MIXPANEL_EVENT.earnings.episodesFilter.opened]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.earnings.episodesFilter.selected]: RevenueDashboardEpisodesFilterSelectedEventsProps;
  [MIXPANEL_EVENT.earnings.totalEstimatedImpressions.tooltipHover]: RevenueDashboardWidgetEventsProps;
};

type DownloadDrawerEventProps = {
  [MIXPANEL_EVENT.earnings.downloadDrawer.opened]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.earnings.downloadDrawer.dateOpened]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.earnings.downloadDrawer.dateApplied]: RevenueDashboardDateRangeEventsProps;
  [MIXPANEL_EVENT.earnings.downloadDrawer.datePresetSelected]: RevenueDashboardDateRangePresetEventsProps;
  [MIXPANEL_EVENT.earnings.downloadDrawer.downloadSuccess]: RevenueDashboardDownloadDrawerDownloadEventsProps;
  [MIXPANEL_EVENT.earnings.downloadDrawer.downloadFailed]: RevenueDashboardDownloadDrawerDownloadFailedEventsProps;
};

type ShowPerformanceEventProps = {
  [MIXPANEL_EVENT.earnings.showPerformance.moreDetailsClicked]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.earnings.showPerformance.closed]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.earnings.showPerformance.downloadClicked]: RevenueDashboardFilteredDownloadClickedEventsProps;
  [MIXPANEL_EVENT.earnings.showPerformance.dateOpened]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.earnings.showPerformance.dateApplied]: RevenueDashboardDateRangeEventsProps;
  [MIXPANEL_EVENT.earnings.showPerformance.datePresetSelected]: RevenueDashboardDateRangePresetEventsProps;
  [MIXPANEL_EVENT.earnings.showPerformance.tableSortColumnClicked]: RevenueDashboardTableSortColumnClickedEventsProps;
  [MIXPANEL_EVENT.earnings.showPerformance.tablePaginateClicked]: RevenueDashboardTablePaginateEventsProps;
};

type CampaignTypesEventProps = {
  [MIXPANEL_EVENT.earnings.campaignTypes.barChartHover]: RevenueDashboardCampaignTypesBarChartHoverEventsProps;
  [MIXPANEL_EVENT.earnings.campaignTypes.moreDetailsClicked]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.earnings.campaignTypes.modalClosed]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.earnings.campaignTypes.downloadClicked]: RevenueDashboardFilteredDownloadClickedEventsProps;
  [MIXPANEL_EVENT.earnings.campaignTypes.showFilterOpened]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.earnings.campaignTypes.showFilterSelected]: RevenueDashboardShowsFilterSelectedEventsProps;
  [MIXPANEL_EVENT.earnings.campaignTypes.dateOpened]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.earnings.campaignTypes.dateApplied]: RevenueDashboardDateRangeEventsProps;
  [MIXPANEL_EVENT.earnings.campaignTypes.datePresetSelected]: RevenueDashboardDateRangePresetEventsProps;
  [MIXPANEL_EVENT.earnings.campaignTypes.tableSortColumnClicked]: RevenueDashboardTableSortColumnClickedEventsProps;
};

type EarningsCampaignsEventProps = {
  [MIXPANEL_EVENT.earnings.topAdvertisers.moreAdvertisersClicked]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.earnings.campaignsTable.filterClicked]: RevenueDashboardCampaignsTableFilterClickedEventsProps;
  [MIXPANEL_EVENT.earnings.campaignsTable.filterOptionClicked]: RevenueDashboardCampaignsTableFilterOptionClickedEventsProps;
  [MIXPANEL_EVENT.earnings.campaignsTable.sortColumnClicked]: RevenueDashboardTableSortColumnClickedEventsProps;
  [MIXPANEL_EVENT.earnings.campaignsTable.paginateClicked]: RevenueDashboardTablePaginateEventsProps;
};

type PayoutsEventProps = {
  [MIXPANEL_EVENT.payouts.overTimeChart.tooltipOpened]: RevenueDashboardPayoutsOverTimeChartTooltipOpenedEventsProps;
  [MIXPANEL_EVENT.payouts.yearTab.clicked]: RevenueDashboardPayoutsYearSelectedEventsProps;
  [MIXPANEL_EVENT.payouts.tableMonthRow.clicked]: RevenueDashboardPayoutsMonthSelectedEventsProps;
  [MIXPANEL_EVENT.payouts.highlights.includeEarningsTooltipHover]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.payouts.highlights.vendorStatementsDownloadClicked]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.payouts.highlights.vendorStatementsDownloadSuccess]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.payouts.campaignTypes.barChartHover]: RevenueDashboardCampaignTypesBarChartHoverEventsProps;
  [MIXPANEL_EVENT.payouts.campaignTypes.moreDetailsClicked]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.payouts.campaignTypes.downloadClicked]: RevenueDashboardPayoutsFilteredDownloadClickedEventsProps;
  [MIXPANEL_EVENT.payouts.campaignTypes.modalClosed]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.payouts.campaignTypes.tableSortColumnClicked]: RevenueDashboardTableSortColumnClickedEventsProps;
  [MIXPANEL_EVENT.payouts.campaignTypes.payoutDropdownClicked]: RevenueDashboardPayoutsMonthSelectedEventsProps;
  [MIXPANEL_EVENT.payouts.campaignsTable.filterClicked]: RevenueDashboardCampaignsTableFilterClickedEventsProps;
  [MIXPANEL_EVENT.payouts.campaignsTable.filterOptionClicked]: RevenueDashboardCampaignsTableFilterOptionClickedEventsProps;
  [MIXPANEL_EVENT.payouts.campaignsTable.sortColumnClicked]: RevenueDashboardTableSortColumnClickedEventsProps;
  [MIXPANEL_EVENT.payouts.campaignsTable.paginateClicked]: RevenueDashboardTablePaginateEventsProps;
};

type AudiencePageEventProps = {
  [MIXPANEL_EVENT.audience.showsFilter.opened]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.audience.showsFilter.selected]: RevenueDashboardShowsFilterSelectedEventsProps;
  [MIXPANEL_EVENT.audience.date.opened]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.audience.date.applied]: RevenueDashboardDateRangeEventsProps;
  [MIXPANEL_EVENT.audience.date.presetSelected]: RevenueDashboardDateRangePresetEventsProps;
  [MIXPANEL_EVENT.audience.demographics.platformRadioSelected]: RevenueDashboardDemographicsRadioSelectedEventsProps;
  [MIXPANEL_EVENT.audience.demographics.chartTooltipHover]: RevenueDashboardDemographicsChartTooltipHoverEventsProps;
  [MIXPANEL_EVENT.audience.socialProfiles.connectMarketplaceProfilesClicked]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.audience.ageModal.opened]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.audience.ageModal.downloadClicked]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.audience.genderModal.opened]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.audience.genderModal.downloadClicked]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.audience.locationModal.opened]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.audience.locationModal.downloadClicked]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.audience.locationModal.hideSocialsToggled]: RevenueDashboardAudienceModalToggleEventsProps;
  [MIXPANEL_EVENT.audience.topPlayersModal.opened]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.audience.topPlayersModal.downloadClicked]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.audience.topPlayersModal.granularityChanged]: RevenueDashboardAudienceModalGranularityEventsProps;
  [MIXPANEL_EVENT.audience.topPlayersModal.hideSocialsToggled]: RevenueDashboardAudienceModalToggleEventsProps;
  [MIXPANEL_EVENT.audience.topPlayersModal.rowSelectionToggled]: RevenueDashboardAudienceModalRowSelectionEventsProps;
  [MIXPANEL_EVENT.audience.topPlayersModal.selectAllToggled]: RevenueDashboardAudienceModalSelectAllEventsProps;
  [MIXPANEL_EVENT.audience.topDevicesModal.opened]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.audience.topDevicesModal.downloadClicked]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.audience.topDevicesModal.deviceSelectionToggled]: RevenueDashboardAudienceModalDeviceSelectionEventsProps;
  [MIXPANEL_EVENT.audience.downloadDrawer.opened]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.audience.downloadDrawer.downloadSuccess]: RevenueDashboardAudienceDownloadDrawerDownloadEventsProps;
  [MIXPANEL_EVENT.audience.downloadDrawer.downloadFailed]: RevenueDashboardAudienceDownloadDrawerDownloadFailedEventsProps;
};

type ContentPerformanceEventProps = {
  [MIXPANEL_EVENT.contentPerformance.showsFilter.opened]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.contentPerformance.showsFilter.selected]: RevenueDashboardShowsFilterSelectedEventsProps;
  [MIXPANEL_EVENT.contentPerformance.date.opened]: RevenueDashboardWidgetEventsProps;
  [MIXPANEL_EVENT.contentPerformance.date.applied]: RevenueDashboardDateRangeEventsProps;
  [MIXPANEL_EVENT.contentPerformance.date.presetSelected]: RevenueDashboardDateRangePresetEventsProps;
  [MIXPANEL_EVENT.contentPerformance.chart.granularityChanged]: RevenueDashboardContentPerformanceChartGranularityEventsProps;
  [MIXPANEL_EVENT.contentPerformance.chart.publishDatesToggled]: RevenueDashboardContentPerformanceChartToggleEventsProps;
  [MIXPANEL_EVENT.contentPerformance.chart.displayTotalToggled]: RevenueDashboardContentPerformanceChartToggleEventsProps;
  [MIXPANEL_EVENT.contentPerformance.chart.tabChanged]: RevenueDashboardContentPerformanceChartTabEventsProps;
  [MIXPANEL_EVENT.contentPerformance.levelTabs.tabChanged]: RevenueDashboardContentPerformanceLevelTabEventsProps;
  [MIXPANEL_EVENT.contentPerformance.chart.launchWindowChanged]: RevenueDashboardContentPerformanceChartLaunchWindowEventsProps;
  [MIXPANEL_EVENT.contentPerformance.chart.postLaunchTooltipHover]: RevenueDashboardContentPerformanceChartPostLaunchTooltipHoverEventsProps;
  [MIXPANEL_EVENT.contentPerformance.episodesTable.sortColumnClicked]: RevenueDashboardTableSortColumnClickedEventsProps;
  [MIXPANEL_EVENT.contentPerformance.episodesTable.paginateClicked]: RevenueDashboardTablePaginateEventsProps;
  [MIXPANEL_EVENT.contentPerformance.episodesTable.pageSizeChanged]: RevenueDashboardContentPerformanceTablePageSizeEventsProps;
  [MIXPANEL_EVENT.contentPerformance.episodeRow.expandToggled]: RevenueDashboardContentPerformanceEpisodeRowEventsProps;
  [MIXPANEL_EVENT.contentPerformance.downloadDrawer.opened]: RevenueDashboardWidgetEventsProps;
  /** @deprecated Legacy `files=` drawer only. TODO: remove once `newDownloadDrawers` is enabled for everyone. */
  [MIXPANEL_EVENT.contentPerformance.downloadDrawer.dateOpened]: RevenueDashboardWidgetEventsProps;
  /** @deprecated Legacy `files=` drawer only. TODO: remove once `newDownloadDrawers` is enabled for everyone. */
  [MIXPANEL_EVENT.contentPerformance.downloadDrawer.dateApplied]: RevenueDashboardDateRangeEventsProps;
  /** @deprecated Legacy `files=` drawer only. TODO: remove once `newDownloadDrawers` is enabled for everyone. */
  [MIXPANEL_EVENT.contentPerformance.downloadDrawer.datePresetSelected]: RevenueDashboardDateRangePresetEventsProps;
  [MIXPANEL_EVENT.contentPerformance.downloadDrawer.downloadSuccess]: RevenueDashboardContentPerformanceDownloadDrawerDownloadEventsProps;
  [MIXPANEL_EVENT.contentPerformance.downloadDrawer.downloadFailed]: RevenueDashboardContentPerformanceDownloadDrawerDownloadFailedEventsProps;
};

type WebVitalsEventProps = {
  [MIXPANEL_EVENT.webVitals.reported]: RevenueDashboardWebVitalsEventsProps;
};

type MixpanelEventPropsMap = RageClickEventProps &
  NetworkSelectEventProps &
  EarningsPageEventProps &
  DownloadDrawerEventProps &
  ShowPerformanceEventProps &
  CampaignTypesEventProps &
  EarningsCampaignsEventProps &
  PayoutsEventProps &
  AudiencePageEventProps &
  ContentPerformanceEventProps &
  WebVitalsEventProps;

export type MixpanelEventProps<T extends MixpanelEvent> = MixpanelEventPropsMap[T];
